import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { union, without } from 'lodash-es';

import { CommercialInvitation, InvitationStatusType, InvitationTokenType } from '@vi-iot/client';

import { InvitationsActions } from './invitations.actions';
import { ActiveInvitationsFilterType } from './invitations.model';
import { InvitationsState } from './invitations.state';

export const initialState: InvitationsState = {
  invitations: {},
  buildingManagerInvitations: [],
  transferInstallationPendingInvitations: [],
  ownerDetails: {},
  commercialInvitations: {},
  installationDetails: {},
  invitationsIds: {
    all: [],
    incoming: [],
    pending: [],
    declined: [],
    expired: [],
    accepted: [],
    terminated: [],
    canceled: [],
  },
  userInvitations: [],
  activeFilter: ActiveInvitationsFilterType.Incoming,
  incomingCount: null,
  isData: false,
  loading: false,
  loaded: false,
  error: null,
};

/* eslint-disable */
export const invitationsReducer: ActionReducer<InvitationsState, Action> = createReducer(
  initialState,
  on(InvitationsActions.listInstallationInvitations, (state) => ({
    ...state,
    loading: true,
    loaded: false,
    error: null,
  })),
  on(InvitationsActions.bulkListInvitations, (state) => ({
    ...state,
    loaded: false,
    loading: true,
    invitations: {},
    invitationsIds: { ...initialState.invitationsIds },
    error: null,
    isData: false,
    activeFilter: ActiveInvitationsFilterType.Incoming,
  })),
  on(InvitationsActions.bulkSaveBuildingMangerInvitations, (state) => ({
    ...state,
    loaded: false,
    loading: true,
  })),
  on(
    InvitationsActions.bulkListInvitationsSuccess,
    (state, { invitations, buildingManagerInvitations }) => {
      const {
        pending,
        expired,
        declined,
        accepted,
        terminated,
        canceled,
        companyId,
        userId,
      } = invitations;
      const pendingData = pending?.data;
      const declinedData = declined?.data;
      const expiredData = expired?.data;
      const acceptedData = accepted?.data;
      const terminatedData = terminated?.data;
      const canceledData = canceled?.data;

      if (pendingData) {
        pendingData.forEach((invitation) => {
          state.invitations[invitation.token] = invitation;
        });
      }

      if (expiredData) {
        expiredData.forEach((invitation) => {
          state.invitations[invitation.token] = invitation;
        });
      }

      if (declinedData) {
        declinedData.forEach((invitation) => {
          state.invitations[invitation.token] = invitation;
        });
      }

      if (acceptedData) {
        acceptedData.forEach((invitation) => {
          state.invitations[invitation.token] = invitation;
        });
      }

      if (terminatedData) {
        terminatedData.forEach((invitation) => {
          state.invitations[invitation.token] = invitation;
        });
      }

      if (canceledData) {
        canceledData.forEach((invitation) => {
          state.invitations[invitation.token] = invitation;
        });
      }

      const all = [
        ...pending.data.map((invitation) => invitation.token),
        // Do not display DECLINED and EXPIRED invitations for now
        // ... expired.data.map(invitation => invitation.token),
        // ... declined.data.map(invitation => invitation.token)
      ];

      const invitingMyself = (inviterCustomerNumber, inviterAccount) => {
        return inviterCustomerNumber === companyId || inviterAccount === userId;
      };

      const incoming = pendingData
        ? pendingData
            .filter(
              (invitation) =>
                invitation.tokenType === InvitationTokenType.Invited &&
                !invitingMyself(invitation.inviterCustomerNumber, invitation.inviterAccount)
            )
            .map((invitation) => invitation.token)
        : [];

      const pendingToAdd = pendingData
        ? pendingData
            .filter(
              (invitation) =>
                invitation.tokenType === InvitationTokenType.Requested ||
                invitingMyself(invitation.inviterCustomerNumber, invitation.inviterAccount)
            )
            .map((invitation) => invitation.token)
        : [];

      const declinedToAdd = declinedData ? declinedData.map((invitation) => invitation.token) : [];

      const expiredToAdd = expiredData ? expiredData.map((invitation) => invitation.token) : [];

      const acceptedToAdd = acceptedData ? acceptedData.map((invitation) => invitation.token) : [];

      const terminatedToAdd = terminatedData
        ? terminatedData.map((invitation) => invitation.token)
        : [];

      const canceledToAdd = canceledData ? canceledData.map((invitation) => invitation.token) : [];

      return {
        ...state,
        buildingManagerInvitations,
        invitationsIds: {
          all: union(state.invitationsIds.all, all),
          incoming: union(state.invitationsIds.incoming, incoming),
          pending: union(state.invitationsIds.pending, pendingToAdd),
          declined: union(state.invitationsIds.declined, declinedToAdd),
          expired: union(state.invitationsIds.expired, expiredToAdd),
          accepted: union(state.invitationsIds.accepted, acceptedToAdd),
          terminated: union(state.invitationsIds.terminated, terminatedToAdd),
          canceled: union(state.invitationsIds.canceled, canceledToAdd),
        },
        isData: !!all.length,
        loading: false,
        loaded: true,
      };
    }
  ),
  on(InvitationsActions.bulkListInvitationsError, (state) => ({
    ...state,
    loading: false,
    loaded: true,
    error: 'Some error occured',
  })),
  on(InvitationsActions.getUserInvitations, (state) => ({
    ...state,
  })),
  on(InvitationsActions.getUserInvitationsSuccess, (state, { data }) => {
    return { ...state, userInvitations: data, loading: false, loaded: true, error: null };
  }),
  on(InvitationsActions.getUserInvitationsError, (state) => ({
    ...state,
    loading: false,
    loaded: true,
  })),
  on(
    InvitationsActions.listPendingTransferInstallationInvitationsSuccess,
    (state, { pendingInvitations }): InvitationsState => ({
      ...state,
      transferInstallationPendingInvitations: pendingInvitations,
    })
  ),
  on(InvitationsActions.getIncomingInvitationsCount, (state) => ({
    ...state,
  })),
  on(InvitationsActions.getIncomingInvitationsCountSuccess, (state, { count }) => ({
    ...state,
    incomingCount: count,
  })),
  on(InvitationsActions.getIncomingInvitationsCountError, (state) => ({
    ...state,
    incomingCount: null,
  })),
  on(InvitationsActions.filterInvitations, (state, { activeFilter }) => ({
    ...state,
    activeFilter,
  })),
  on(InvitationsActions.getInvitationOwnerDetailsSuccess, (state, { token, response }) => {
    return {
      ...state,
      ownerDetails: {
        ...state.ownerDetails,
        [token]: response.data,
      },
    };
  }),
  on(InvitationsActions.getInvitationInstallationDetailsSuccess, (state, { token, response }) => {
    return {
      ...state,
      installationDetails: {
        ...state.installationDetails,
        [token]: response.data,
      },
    };
  }),
  on(InvitationsActions.getCommercialInvitationDetails.success, (state, { token, data }) => {
    return {
      ...state,
      commercialInvitations: {
        ...state.commercialInvitations,
        [token]: data,
      },
    };
  }),
  on(InvitationsActions.addPendingInvitation, (state, { invitation, gatewaySerial }) => {
    if (state.invitationsIds.all.length === 0) {
      state.activeFilter = ActiveInvitationsFilterType.All;
      state.isData = true;
    }
    state.invitations[invitation.token] = invitation;
    state.invitationsIds.all.push(invitation.token);
    state.invitationsIds.pending.push(invitation.token);
    state.installationDetails[invitation.token] = <any>{
      gateways: [
        {
          serial: gatewaySerial,
        },
      ],
    };

    return {
      ...state,
    };
  }),
  on(InvitationsActions.acceptIncomingInvitation, (state, { invitation }) => {
    state.invitationsIds.all = without(state.invitationsIds.all, invitation.token);
    state.invitationsIds.incoming = without(state.invitationsIds.incoming, invitation.token);

    return {
      ...state,
      incomingCount: state.invitationsIds.incoming.length,
    };
  }),
  on(InvitationsActions.rejectIncomingInvitation, (state, { invitation }) => {
    // Remove `Rejected` invitations from `All` until `Declined` will be shown again
    state.invitationsIds.all = without(state.invitationsIds.all, invitation.token);

    state.invitationsIds.incoming = without(state.invitationsIds.incoming, invitation.token);
    state.invitationsIds.declined.push(invitation.token);
    state.isData = !!state.invitationsIds.all.length;

    return {
      ...state,
      incomingCount: state.invitationsIds.incoming.length,
    };
  }),
  on(InvitationsActions.cancelInvitation, (state, { invitation }) => {
    // Remove `Canceled` invitations from `All` until `Declined` will be shown again
    state.invitationsIds.all = without(state.invitationsIds.all, invitation.token);
    state.invitationsIds.pending = without(state.invitationsIds.pending, invitation.token);

    state.invitationsIds.incoming = without(state.invitationsIds.incoming, invitation.token);
    state.invitationsIds.declined.push(invitation.token);
    state.isData = !!state.invitationsIds.all.length;

    return {
      ...state,
      incomingCount: state.invitationsIds.incoming.length,
    };
  }),

  on(InvitationsActions.updateCommercialInvitation.success, (state, { token, status }) => {
    if (status === InvitationStatusType.Rejected) {
      const invitationsToUpdate = structuredClone(state.commercialInvitations);
      delete invitationsToUpdate[token];
      let isData = Object.keys(invitationsToUpdate).length !== 0;

      return {
        ...state,
        commercialInvitations: invitationsToUpdate,
        isData: isData,
        incomingCount: state.incomingCount - 1,
      };
    }

    const invitationToUpdate: CommercialInvitation = structuredClone(
      state.commercialInvitations[token]
    );

    invitationToUpdate.status = status;

    return {
      ...state,
      commercialInvitations: {
        ...state.commercialInvitations,
        [token]: invitationToUpdate,
      },
      incomingCount: state.incomingCount - 1,
    };
  })
);
